import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/logo.png";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { User, Heart, PlusCircle, Calendar, HelpCircle, LogOut, Menu, X, Shield } from "lucide-react";

export function Header() {
  const { user, signOut, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const isHome = location.pathname === "/";
  const adminOnlyMenu =
    isAdmin && isHome && user?.email?.toLowerCase() === "pauloisidoro82@gmail.com";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { setAvatarUrl(null); return; }
      const { data } = await supabase
        .from("profiles")
        .select("avatar_url")
        .eq("user_id", user.id)
        .maybeSingle();
      const path = data?.avatar_url;
      if (!path) { if (!cancelled) setAvatarUrl(null); return; }
      const { data: signed } = await supabase.storage
        .from("avatars")
        .createSignedUrl(path, 60 * 60);
      if (!cancelled) setAvatarUrl(signed?.signedUrl ?? null);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const getInitials = (name?: string | null, email?: string | null) => {
    if (name && name.trim()) {
      const parts = name.trim().split(" ");
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return parts[0][0].toUpperCase();
    }
    if (email) return email[0].toUpperCase();
    return "U";
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "";
  const initials = getInitials(user?.user_metadata?.full_name, user?.email);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <motion.header 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b border-border"
    >
      <div className="container mx-auto px-4 lg:px-8">
        <nav className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="Plateia.fun" className="h-20 lg:h-24" />
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            {user ? (
              <>
                <Link 
                  to="/eventos" 
                  className="text-foreground/80 hover:text-primary transition-colors font-medium"
                >
                  Eventos
                </Link>
                <Link 
                  to="/dashboard/meus-ingressos" 
                  className="text-foreground/80 hover:text-primary transition-colors font-medium"
                >
                  Meus ingressos
                </Link>
              </>
            ) : (
              <>
                <Link 
                  to="/" 
                  className="text-foreground/80 hover:text-primary transition-colors font-medium"
                >
                  Home
                </Link>
                <Link 
                  to="/eventos" 
                  className="text-foreground/80 hover:text-primary transition-colors font-medium"
                >
                  Eventos
                </Link>
                <Link 
                  to="/criar-conta" 
                  className="text-foreground/80 hover:text-primary transition-colors font-medium"
                >
                  Criar Conta
                </Link>
                <Link 
                  to="/entrar" 
                  className="text-foreground/80 hover:text-primary transition-colors font-medium"
                >
                  Entrar
                </Link>
              </>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Mobile hamburger for non-logged-in users */}
            {!loading && !user && (
              <button
                type="button"
                aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
                aria-expanded={mobileOpen}
                className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            )}

            {!loading && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0 border-2 border-primary/30 hover:border-primary">
                    <Avatar className="h-10 w-10">
                      {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
                      <AvatarFallback className="bg-primary/10 text-primary font-bold text-sm">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64" align="end" sideOffset={8}>
                  <div className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
                        <AvatarFallback className="bg-primary/10 text-primary font-bold text-sm">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{displayName}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  {adminOnlyMenu ? (
                    <DropdownMenuItem onClick={() => navigate("/admin")} className="gap-3 cursor-pointer">
                      <Shield size={16} />
                      Painel Admin
                    </DropdownMenuItem>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/dashboard/perfil")} className="gap-3 cursor-pointer">
                        <User size={16} />
                        Minha conta
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/favoritos")} className="gap-3 cursor-pointer">
                        <Heart size={16} />
                        Favoritos
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/dashboard/eventos/criar")} className="gap-3 cursor-pointer">
                        <PlusCircle size={16} />
                        Criar evento
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/dashboard/eventos")} className="gap-3 cursor-pointer">
                        <Calendar size={16} />
                        Meus eventos
                      </DropdownMenuItem>
                      {isAdmin && (
                        <DropdownMenuItem onClick={() => navigate("/admin")} className="gap-3 cursor-pointer">
                          <Shield size={16} />
                          Painel Admin
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/dashboard/ajuda")} className="gap-3 cursor-pointer">
                        <HelpCircle size={16} />
                        Central de Ajuda
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="gap-3 cursor-pointer text-destructive">
                    <LogOut size={16} />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : !loading ? (
              <Link to="/entrar" className="hidden md:block">
                <Button variant="wine">
                  Entrar
                </Button>
              </Link>
            ) : null}
          </div>
        </nav>

        {/* Mobile menu for non-logged-in users */}
        {mobileOpen && !user && !loading && (
          <div className="md:hidden border-t border-border bg-card/95 backdrop-blur-md px-4 pb-4 pt-2 space-y-2">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="block py-2 text-foreground/80 hover:text-primary transition-colors font-medium"
            >
              Home
            </Link>
            <Link
              to="/eventos"
              onClick={() => setMobileOpen(false)}
              className="block py-2 text-foreground/80 hover:text-primary transition-colors font-medium"
            >
              Eventos
            </Link>
            <Link
              to="/entrar"
              onClick={() => setMobileOpen(false)}
              className="block py-2 text-foreground/80 hover:text-primary transition-colors font-medium"
            >
              Entrar
            </Link>
            <Link to="/criar-conta" onClick={() => setMobileOpen(false)}>
              <Button variant="wine" className="w-full mt-2">
                Crie sua conta gratuitamente
              </Button>
            </Link>
          </div>
        )}
      </div>
    </motion.header>
  );
}
